import { loadSound } from "./sound";

const IMAGE_BASE = "assets/images/";
const SOUND_BASE = "assets/sounds/";
const FONT_BASE = "assets/fonts/";

const images = new Map<string, HTMLImageElement>();

/** Image for an asset file name (e.g. `231_SPR_knight.png`). Must be preloaded. */
export function imageAsset(file: string): HTMLImageElement | undefined {
  return images.get(file);
}

export interface AssetManifest {
  images: string[];
  sounds: string[];
  fonts: { family: string; file: string }[];
}

export type ProgressFn = (loaded: number, total: number) => void;

export async function preload(
  manifest: AssetManifest,
  onProgress?: ProgressFn,
): Promise<void> {
  const total =
    manifest.images.length + manifest.sounds.length + manifest.fonts.length;
  let loaded = 0;
  const tick = () => onProgress?.(++loaded, total);

  const fontTasks = manifest.fonts.map(async ({ family, file }) => {
    const face = new FontFace(
      family,
      `url(${FONT_BASE}${encodeURIComponent(file)})`,
    );
    try {
      document.fonts.add(await face.load());
    } catch {
      // fall back to a system face rather than failing the boot
    }
    tick();
  });

  const imageTasks = manifest.images.map((file) => {
    const { promise, resolve } = Promise.withResolvers<void>();
    const img = new Image();
    const done = (ok: boolean) => {
      if (ok) images.set(file, img);
      tick();
      resolve();
    };
    img.onload = () => done(true);
    img.onerror = () => done(false);
    img.src = IMAGE_BASE + encodeURIComponent(file);
    return promise;
  });

  await Promise.all([...fontTasks, ...imageTasks]);

  // Audio decoding is deferred behind the images so the first frame paints
  // sooner; music files are large and nothing plays before the intro.
  await Promise.all(
    manifest.sounds.map(async (id) => {
      await loadSound(id, [`${SOUND_BASE}${id}.ogg`, `${SOUND_BASE}${id}.m4a`]);
      tick();
    }),
  );
}
